/**
 * The Statsig client SDK key, or `undefined` when it is not configured.
 *
 * Deliberately not a hard `zString().parse(...)` at module load the way
 * `PUBLIC_SEGMENT_KEY` is: Statsig is a POC, and a throw here would break local
 * dev and every deploy for anyone whose `.env-web` predates the key. Callers
 * treat `undefined` as "Statsig is off" and degrade gracefully.
 */
export function getStatsigClientKey(): string | undefined {
  const key = process.env.PUBLIC_STATSIG_CLIENT_KEY;

  return key === undefined || key === '' ? undefined : key;
}

type StatsigProxyUrlConfig =
  | { status: 'unset' }
  | { status: 'configured'; url: string }
  | { status: 'invalid'; value: string };

const PROXY_URL_PROTOCOLS = new Set(['https:', 'http:']);

/**
 * `PUBLIC_STATSIG_PROXY_URL`, classified.
 *
 * Optional for the same reason the client key is: unset means "exactly as
 * before", so local dev and any deploy that predates the proxy keep working.
 * There is deliberately no runtime switch in front of this: a flag would have
 * to be fetched before the SDK could initialize, on every surface, and the
 * rollback it buys — unsetting the variable and deploying — is the same lever
 * as for any other env-configured dependency.
 *
 * A value without an `http(s)://` scheme is `invalid` rather than repaired.
 * The SDK is handed full endpoint URLs, so a bare host would be fetched as a
 * path relative to the page — `/bananasplit.hungryroot.com/v1/scoop` on our
 * own origin — and every initialize would 404 while the proxy silently did
 * nothing. Treating it as unset degrades to Statsig's own domains, the known
 * working path, and leaves the mistake to `useReportInvalidProxyUrl` to make
 * visible instead of letting the SDK's own fallback paper over it.
 */
export function readStatsigProxyUrl(): StatsigProxyUrlConfig {
  const value = process.env.PUBLIC_STATSIG_PROXY_URL?.trim();

  if (value === undefined || value === '') {
    return { status: 'unset' };
  }

  if (!hasHttpScheme(value)) {
    return { status: 'invalid', value };
  }

  // A trailing slash would produce `//v1/scoop`, a path the proxy answers
  // with a 404.
  return { status: 'configured', url: value.replace(/\/+$/, '') };
}

function hasHttpScheme(value: string): boolean {
  try {
    return PROXY_URL_PROTOCOLS.has(new URL(value).protocol);
  } catch {
    return false;
  }
}

/**
 * Origin of the first-party proxy for the Statsig client SDK, or `undefined`
 * when the SDK should talk to Statsig's own domains — because no proxy is
 * configured, or because the configured value is unusable.
 */
export function getStatsigProxyUrl(): string | undefined {
  const config = readStatsigProxyUrl();

  return config.status === 'configured' ? config.url : undefined;
}

const DEVELOPMENT_TIER = 'development';

/**
 * Environment tier for Statsig targeting rules, so console-side rules can
 * separate staging traffic from production without a code change.
 */
export function getStatsigEnvironmentTier(): string {
  return process.env.PUBLIC_HR_ENV ?? DEVELOPMENT_TIER;
}

/**
 * Whether to trade Statsig's payload obfuscation for traffic a developer can
 * actually read in the network panel.
 *
 * By default the SDK base64-encodes and reverses request bodies, which makes
 * confirming an assignment by inspection impractical. Only that encoding is
 * turned off, and only in development, since the obfuscation exists to survive
 * ad blockers. Response name hashing stays on — see `StatsigBoundary` for why
 * asking for plaintext names breaks config lookup outright.
 */
export function isStatsigReadableRequestsEnabled(): boolean {
  return getStatsigEnvironmentTier() === DEVELOPMENT_TIER;
}

/**
 * Whether Statsig is configured at all. Check it before generating an identity
 * in an SSR page's frontmatter, so that an environment without keys behaves
 * exactly as it did before Statsig was introduced, rather than setting a cookie
 * nothing reads.
 */
export function isStatsigEnabled(): boolean {
  return getStatsigClientKey() !== undefined;
}
